/**
 * SOLID Review
 * S — Acceptable. The landing screen's presentation: the entry funnel's first step, the account row and the join error the session
 *     holds, the three modals' open state, and the cups' animation ORCHESTRATION — when the sequence plays (on arrival, unless the
 *     device is weak or the player asked for less motion; on a tap, with its sound), how long it runs — over a stage the view draws
 *     and replays.
 * O — Acceptable. A new modal is one more flag; the timeline is the stage's data.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Acceptable. The funnel's three actions, the modals' openers and closers, the shake.
 * D — Strong. The Application's use cases and view, the request contexts, the platform's timers, device and style sheets, the
 *     sound, the consent controller and the stage port.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { IConfigDepViewPlatform, TViewTimer } from './platform/IConfigDepViewPlatform';
import type { DiceRollingSound } from './DiceRollingSound';
import type { ConsentModalController } from './ConsentModalController';
import type { ClientRequests } from '../client/ClientRequests';
import { SEQUENCE_MS, SHAKE_DELAY_S, WELCOME_KEYFRAMES, WELCOME_KEYFRAMES_ID } from '../components/auth/welcomeScreen/welcomeStage';
import type { IPortClientStateView, TClientViewState } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

const TO_ENTER_CODE: TClientUseCaseInput<'navigate'> = { screen: 'ENTER_CODE' };
const DISMISS: TClientUseCaseInput<'dismissJoinError'> = {};
const SIGN_OUT: TClientUseCaseInput<'signOut'> = {};

type TSession = TClientViewState['session'];

interface IStateWelcomeOwn {
  readonly animating: boolean;
  readonly rulesOpen: boolean;
  readonly profileOpen: boolean;
  readonly legalOpen: boolean;
}

/** The landing screen as its view renders it. */
export interface IStateWelcome extends IStateWelcomeOwn {
  /** A logged-in player: the profile icon and modal. A guest: the status and logout row. */
  readonly registered: boolean;
  readonly isGuest: boolean;
  readonly displayName: TSession['account']['displayName'];
  readonly pendingJoinError: TSession['pendingJoinError'];
  /** A weak device gets the flat-colour cups, and no animation. */
  readonly lowEnd: boolean;
}

/** The screen's stage as its view draws it: the seven animated elements, replayed in place. */
export interface IServiceWelcomeStage {
  /** Restarts every animation of the stage without remounting it — a remount made WebKit's SVG gradient references go stale. */
  restart(): void;
}

export interface IConfigDepWelcome {
  readonly useCases: Pick<IPortClientUseCases, 'navigate' | 'dismissJoinError' | 'signOut'>;
  readonly view: IPortClientStateView;
  readonly requests: ClientRequests;
  readonly platform: Pick<IConfigDepViewPlatform, 'timers' | 'device' | 'styles'>;
  readonly sound: DiceRollingSound;
  readonly consent: ConsentModalController;
  readonly stage: IServiceWelcomeStage;
}

const AT_REST: IStateWelcomeOwn = { animating: false, rulesOpen: false, profileOpen: false, legalOpen: false };
const ANIMATING: Partial<IStateWelcomeOwn> = { animating: true };
const STILL: Partial<IStateWelcomeOwn> = { animating: false };
const RULES_OPEN: Partial<IStateWelcomeOwn> = { rulesOpen: true };
const RULES_CLOSED: Partial<IStateWelcomeOwn> = { rulesOpen: false };
const PROFILE_OPEN: Partial<IStateWelcomeOwn> = { profileOpen: true };
const PROFILE_CLOSED: Partial<IStateWelcomeOwn> = { profileOpen: false };
const LEGAL_OPEN: Partial<IStateWelcomeOwn> = { legalOpen: true };
const LEGAL_CLOSED: Partial<IStateWelcomeOwn> = { legalOpen: false };

/**
 * THE LANDING SCREEN (frontend-framework-oo P3, 2026-10-29, §28.295) — `components/auth/WelcomeScreen.tsx`'s state, its mount effect,
 * its animation restart and its handlers. The cups play on arrival unless the device is weak (≤ 1 GB or ≤ 2 cores: SVG paint-server
 * gradients and CSS animation cause memory pressure there) or the player asked the system for less motion; a tap replays them from
 * the start, with the sound, mid-sequence too. The stage's keyframes are added to the page when the screen first shows (the module
 * added them when imported). The weak-device answer is read once, when the screen is made (the module read it once, at import).
 */
export class WelcomeViewModel implements IServiceViewModel<IStateWelcome> {
  private readonly state = new ObservableState<IStateWelcomeOwn>(AT_REST);
  private readonly lowEnd: boolean;
  private timer: TViewTimer | null = null;
  private audio: AudioContext | null = null;
  private readonly reactions = new Reactions<null>([{
    deps: () => [],
    run: () => {
      if (!this.deps.platform.device.prefersReducedMotion() && !this.lowEnd) this.shake();
      return () => {
        if (this.timer !== null) this.deps.platform.timers.clearTimeout(this.timer);
        this.audio?.close().catch(() => { /* already closed */ });
      };
    },
  }]);
  private shown: {
    readonly own: IStateWelcomeOwn; readonly kind: TSession['account']['kind']; readonly displayName: IStateWelcome['displayName'];
    readonly pendingJoinError: IStateWelcome['pendingJoinError']; readonly screen: IStateWelcome;
  } | null = null;

  constructor(private readonly deps: IConfigDepWelcome) {
    this.lowEnd = deps.platform.device.lowEnd();
    this.manageCookies = deps.consent.openPreferences;
  }

  /** The cups' sequence from the start — a tap mid-sequence restarts it; with sound, the last sound's context is closed first. */
  readonly shake = (withSound = false): void => {
    const { timers } = this.deps.platform;
    if (this.timer !== null) timers.clearTimeout(this.timer);
    this.state.update(ANIMATING);
    this.deps.stage.restart();
    if (withSound) {
      // A replay tap would otherwise leave the previous context's delayed rattle scheduled on top of the new one.
      this.audio?.close().catch(() => { /* already closed */ });
      this.audio = this.deps.sound.play(SHAKE_DELAY_S);
    }
    this.timer = timers.setTimeout(() => this.state.update(STILL), SEQUENCE_MS);
  };

  /** "Play Meyer!": the funnel's next step — the game-table code. */
  readonly play = (): void => {
    void this.deps.useCases.navigate(this.deps.requests.context(), TO_ENTER_CODE);
  };

  readonly dismissJoinError = (): void => {
    void this.deps.useCases.dismissJoinError(this.deps.requests.context(), DISMISS);
  };

  /** A guest's logout: the guest session goes and the screen stays, so the next "Play Meyer!" offers guest or login again. */
  readonly logout = (): void => {
    void this.deps.useCases.signOut(this.deps.requests.context(), SIGN_OUT);
  };

  readonly openRules = (): void => this.state.update(RULES_OPEN);
  readonly closeRules = (): void => this.state.update(RULES_CLOSED);
  readonly openProfile = (): void => this.state.update(PROFILE_OPEN);
  readonly closeProfile = (): void => this.state.update(PROFILE_CLOSED);
  readonly openLegal = (): void => this.state.update(LEGAL_OPEN);
  readonly closeLegal = (): void => this.state.update(LEGAL_CLOSED);
  /** The legal modal's "Manage cookies": the cookie preferences' own opener. */
  readonly manageCookies: () => void;

  /** The screen — the same object until its own state or one of the three session reads changes. */
  getSnapshot(): IStateWelcome {
    const own = this.state.get();
    const session = this.deps.view.getSnapshot().session;
    const kind = session.account.kind;
    const { displayName } = session.account;
    const { pendingJoinError } = session;
    const shown = this.shown;
    if (shown !== null && shown.own === own && shown.kind === kind && shown.displayName === displayName
      && shown.pendingJoinError === pendingJoinError) return shown.screen;
    const screen: IStateWelcome = {
      ...own, registered: kind === 'registered', isGuest: kind === 'guest', displayName, pendingJoinError, lowEnd: this.lowEnd,
    };
    this.shown = { own, kind, displayName, pendingJoinError, screen };
    return screen;
  }

  subscribe(listener: () => void): () => void {
    const leaveOwn = this.state.subscribe(listener);
    const leaveView = this.deps.view.subscribe(listener);
    return () => {
      leaveOwn();
      leaveView();
    };
  }

  attach(): void {
    this.deps.platform.styles.ensure(WELCOME_KEYFRAMES_ID, WELCOME_KEYFRAMES);
  }

  detach(): void {
    this.reactions.stop();
  }

  release(): void {
    this.reactions.stopChanged(null);
  }

  commit(): void {
    this.reactions.startChanged(null);
  }
}
